/**
 * The HTTP client (00-contracts §12, §14.1, §14.4).
 *
 * One function talks to the API. It injects `Authorization` from the live Supabase session on
 * every call (never from a module-level cache), attaches `X-Tvitvi-Organization` from the
 * active-organization store, mints an `X-Request-Id`, retries exactly once after refreshing an
 * expired token, and parses every response with the schema the API serialised it with — so a
 * contract drift surfaces here rather than three components deep.
 */
import {
  API_VERSION_PREFIX,
  ORGANIZATION_HEADER,
  REQUEST_ID_HEADER,
  errorResponseSchema,
} from '@tvitvi/shared';
import type { z } from 'zod';

import { getActiveOrganizationId } from '@/auth/active-org';
import { getAccessToken, refreshAccessToken } from '@/auth/supabase';
import { env } from '@/env';

import { ApiError, ContractViolationError, NetworkError, toErrorCode } from './errors';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface ApiRequestOptions {
  path: string;
  method?: HttpMethod;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /**
   * Set false on the endpoints that deliberately span organizations —
   * `GET /v1/organizations` runs with no org context at all (§13.3).
   */
  sendOrganizationHeader?: boolean;
  /**
   * Organisasjonen headeren skal navngi, når den ikke er den aktive. For varslene (P10, funn C):
   * i gjestemodus hører de til personens egen organisasjon (`homeOrganizationIdFor`), så nøkkelen
   * og headeren er den samme organisasjonen — aldri den gjestemodus står i.
   */
  organizationId?: string;
}

/**
 * The absolute URL for a path, exported for the ONE caller that cannot go through `apiRequest`.
 *
 * An upload's body IS the file — `fetch(url, { body: file })` sends the bytes with no copy and no
 * base64, which is why the API takes a raw body rather than multipart. `apiRequest` serialises its
 * body as JSON, so it cannot be that caller; what it CAN still own is the URL and the headers, and
 * this and `uploadHeaders` are those two, so the exception repeats nothing.
 */
export function apiUrl(path: string, query: ApiRequestOptions['query']): string {
  return buildUrl(path, query);
}

/**
 * The headers an upload needs: the same auth and organisation the rest of the app sends, minus the
 * JSON content type — which the caller replaces with the file's own.
 */
export async function uploadHeaders(token?: string | null): Promise<Record<string, string>> {
  const headers = await buildHeaders(
    { path: '' },
    newRequestId(),
    /*
     * Tokenet kan OPPGIS, og det er ikke en bekvemmelighet: etter en fornyelse er den returnerte
     * verdien den eneste som garantert er den nye. `getAccessToken()` leser en lagret økt, og å
     * hvile på at den alt er oppdatert er nøyaktig den slags antakelse som gjør et andre forsøk
     * til en gjentakelse av det første. `apiRequest` har alltid brukt den returnerte verdien.
     */
    token === undefined ? await getAccessToken() : token,
  );
  headers.delete('Content-Type');
  return Object.fromEntries(headers.entries());
}

/**
 * EN FORESPØRSEL DENNE MODULEN IKKE BYGGER, MED DEN SAMME TOKEN-REGELEN SOM ALLE ANDRE.
 *
 * ================================================================================================
 * DETTE ER ROTEN TIL «`POST /v1/attachments` FEILER AV OG TIL» (QA runde 6, punkt 9).
 *
 * `apiRequest` har siden den ble skrevet gjort én ting til etter et 401: lest feilkoden, fornyet
 * tokenet hvis den er `TOKEN_EXPIRED`, og sendt på nytt. Det er derfor ingen har merket at et
 * tilgangstoken utløper mens en fane står åpen.
 *
 * Opplastingen bygger sin EGEN `fetch` — den må, for kroppen er en fil og ikke JSON — og arvet
 * aldri den regelen. Den kastet 401-en rett ut i skjemaet.
 *
 * Og det forklarer nøyaktig ordet «av og til»: feilen kommer an på hvor lenge fanen har stått
 * åpen, ikke på fila, ikke på størrelsen og ikke på hvem som laster opp. Den er ikke gjenskapbar
 * på kommando, den treffer den som har jobbet lengst i produktet, og den ser ut som et nettverk
 * som glipper. Et vedlegg er dessuten nettopp det man gjør sent i en økt.
 *
 * ================================================================================================
 * HELPEREN OG IKKE EN KOPI.
 *
 * `CLAUDE.md`: ett sted per tverrgående hensyn. Å skrive de seks linjene inn i
 * `useUploadAttachment` ville gitt to kopier av «når fornyer vi», og den ene ville en dag blitt
 * rettet alene. Her tar kalleren bare med seg HVORDAN den sender; regelen bor fortsatt her.
 *
 * `send` kalles med ferske hoder begge ganger, fordi hodene bærer tokenet — og det er hele
 * poenget med det andre forsøket.
 */
export async function sendAuthenticated(
  send: (headers: Record<string, string>) => Promise<Response>,
): Promise<Response> {
  const response = await send(await uploadHeaders());
  if (response.status !== 401) return response;

  /*
   * Kroppen leses her, og den kan bare leses én gang. Derfor KASTER denne grenen framfor å gi
   * svaret tilbake: et `Response` med tømt kropp ville gitt kalleren en tom feilmelding.
   */
  const error = await readErrorBody(response, newRequestId());
  if (error.code !== 'TOKEN_EXPIRED') throw error;

  const token = await refreshAccessToken();
  if (!token) throw error;

  return send(await uploadHeaders(token));
}

function buildUrl(path: string, query: ApiRequestOptions['query']): string {
  const url = new URL(`${env.apiUrl}${API_VERSION_PREFIX}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function newRequestId(): string {
  return crypto.randomUUID();
}

async function buildHeaders(
  options: ApiRequestOptions,
  requestId: string,
  token: string | null,
): Promise<Headers> {
  const headers = new Headers({ Accept: 'application/json', [REQUEST_ID_HEADER]: requestId });
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (options.sendOrganizationHeader !== false) {
    const organizationId = options.organizationId ?? getActiveOrganizationId();
    if (organizationId) headers.set(ORGANIZATION_HEADER, organizationId);
  }
  return headers;
}

export async function readErrorBody(
  response: Response,
  fallbackRequestId: string,
): Promise<ApiError> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }
  const parsed = errorResponseSchema.safeParse(payload);
  if (parsed.success) {
    const { code, message, requestId, details } = parsed.data.error;
    return new ApiError(response.status, toErrorCode(code), message, requestId, details ?? []);
  }
  // A non-conforming error body is itself a contract break, but the user still needs an
  // actionable message, so it degrades to the generic code rather than throwing something else.
  return new ApiError(
    response.status,
    response.status === 401 ? 'UNAUTHENTICATED' : 'INTERNAL_ERROR',
    `Unparseable error body from ${response.url}`,
    response.headers.get(REQUEST_ID_HEADER) ?? fallbackRequestId,
  );
}

async function performRequest(options: ApiRequestOptions, token: string | null): Promise<Response> {
  const requestId = newRequestId();
  const headers = await buildHeaders(options, requestId, token);
  try {
    return await fetch(buildUrl(options.path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
      // The bearer token is sent explicitly; cookies would only add CSRF surface.
      credentials: 'omit',
      mode: 'cors',
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(cause);
  }
}

export async function apiRequest<TSchema extends z.ZodType>(
  options: ApiRequestOptions & { schema: TSchema },
): Promise<z.output<TSchema>>;
export async function apiRequest(
  options: ApiRequestOptions & { schema?: undefined },
): Promise<void>;
export async function apiRequest(
  options: ApiRequestOptions & { schema?: z.ZodType },
): Promise<unknown> {
  let token = await getAccessToken();
  let response = await performRequest(options, token);

  if (response.status === 401) {
    const error = await readErrorBody(response, newRequestId());
    // Exactly one retry, and only for an expired token. Retrying TOKEN_INVALID or
    // ACCOUNT_DISABLED would hammer the API on a state that cannot resolve itself.
    if (error.code !== 'TOKEN_EXPIRED') throw error;
    token = await refreshAccessToken();
    if (!token) throw error;
    response = await performRequest(options, token);
  }

  if (!response.ok) throw await readErrorBody(response, newRequestId());

  if (response.status === 204 || options.schema === undefined) {
    // Drain the body so the connection can be reused even when we ignore the payload.
    await response.text();
    return undefined;
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch (cause) {
    throw new ContractViolationError(options.path, cause);
  }

  const parsed = options.schema.safeParse(payload);
  if (!parsed.success) throw new ContractViolationError(options.path, parsed.error);
  return parsed.data;
}
